import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, newKey } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Receipt, Trash2 } from "lucide-react";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n || 0);

const emptyForm = () => ({
  doc_type: "bill",
  employee_id: "",
  buyer_id: "",
  bill_number: "",
  bill_date: new Date().toISOString().slice(0, 10),
  amount: "",
  paid_amount: "",
  notes: "",
  order_status: "pending",
  follow_up_date: "",
  follow_up_employee_id: "",
});

export default function BillForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [employees, setEmployees] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [initialBuyer, setInitialBuyer] = useState(null);
  const [payments, setPayments] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [payMode, setPayMode] = useState("cash");
  const [payNote, setPayNote] = useState("");
  const [payKey, setPayKey] = useState(null);
  const [payBusy, setPayBusy] = useState(false);
  const [createKey] = useState(newKey);
  const { user } = useAuth();
  const isOwner = ["admin", "owner"].includes(user?.role);

  useEffect(() => {
    api.get("/employees").then((r) => setEmployees(r.data)).catch((e) => toast.error(formatApiError(e)));
    if (isEdit) {
      api.get(`/bills/${id}`).then((r) => {
        setInitialBuyer(r.data.buyer || null);
        setPayments(r.data.payments || []);
        setForm({
          doc_type: r.data.doc_type || "bill",
          employee_id: r.data.employee_id,
          buyer_id: r.data.buyer_id,
          bill_number: r.data.bill_number,
          bill_date: r.data.bill_date,
          amount: Math.round(r.data.amount ?? 0),
          paid_amount: Math.round(r.data.paid_amount ?? 0),
          notes: r.data.notes || "",
          order_status: r.data.order_status || "pending",
          follow_up_date: r.data.follow_up_date || "",
          follow_up_employee_id: r.data.follow_up_employee_id || r.data.employee_id || "",
        });
      }).catch((e) => toast.error(formatApiError(e)));
    }
  }, [id, isEdit]);

  useEffect(() => {
    if (!form.employee_id) {
      setBuyers([]);
      return;
    }
    api
      .get("/buyers", { params: { employee_id: form.employee_id } })
      .then((r) => setBuyers(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [form.employee_id]);

  // Ensure the buyer bound to this bill is always present in the dropdown,
  // even if the filtered fetch hasn't run yet or excluded it.
  const buyerOptions = useMemo(() => {
    const list = [...buyers];
    if (initialBuyer && !list.some((b) => b.id === initialBuyer.id)) {
      list.unshift(initialBuyer);
    }
    return list;
  }, [buyers, initialBuyer]);

  const amount = parseInt(form.amount, 10) || 0;
  const paid = parseInt(form.paid_amount, 10) || 0;
  const balance = amount - paid;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id) return toast.error("Choose an employee");
    if (!form.buyer_id) return toast.error("Choose a buyer");
    if (!amount) return toast.error("Enter the amount");
    setBusy(true);
    try {
      const payload = { ...form, amount };
      if (isEdit) {
        delete payload.paid_amount;
        await api.put(`/bills/${id}`, payload);
        toast.success("Updated");
      } else {
        const { data } = await api.post("/bills", { ...payload, paid_amount: paid, idempotency_key: createKey });
        toast.success(`${data.doc_type === "challan" ? "Challan" : "Bill"} ${data.bill_number} created`);
      }
      navigate("/bills");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const applyBill = (data) => {
    setPayments(data.payments || []);
    setForm((f) => ({ ...f, paid_amount: Math.round(data.paid_amount) }));
  };

  const addPayment = async () => {
    if (payBusy) return;
    const amt = parseInt(payAmount, 10) || 0;
    if (amt <= 0) return toast.error("Enter an amount");
    if (amt > balance) return toast.error(`Payment is more than the pending balance ${inr(balance)}`);
    const key = payKey || newKey();
    setPayKey(key);
    setPayBusy(true);
    try {
      const { data } = await api.post(`/bills/${id}/payments`, {
        amount: amt,
        date: payDate,
        mode: payMode,
        note: payNote,
        idempotency_key: key,
      });
      toast.success("Payment recorded");
      setPayKey(null);
      setPayAmount("");
      setPayDate(new Date().toISOString().slice(0, 10));
      setPayMode("cash");
      setPayNote("");
      applyBill(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPayBusy(false);
    }
  };

  const removePayment = async (p) => {
    if (!window.confirm(`Remove payment of ${inr(p.amount)} dated ${(p.date || "").slice(0, 10)}? It stays in the audit log.`)) return;
    try {
      const { data } = await api.delete(`/bills/${id}/payments/${p.id}`);
      toast.success("Payment removed");
      applyBill(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const selectedEmployee = useMemo(
    () => employees.find((e) => e.id === form.employee_id),
    [employees, form.employee_id]
  );

  return (
    <div className="p-8 lg:p-14 max-w-3xl mx-auto">
      <div className="mb-10 animate-fade-up">
        <div className="text-[11px] uppercase tracking-[0.35em] text-neutral-500">
          {isEdit ? "Update Record" : "New Record"}
        </div>
        <h1 className="font-display text-6xl font-bold tracking-tight mt-2">
          {isEdit ? "Edit" : "Record"} {form.doc_type === "challan" ? "Challan" : "Bill"}
        </h1>
      </div>

      <form onSubmit={submit} className="card-surface p-8 space-y-7 stagger">
        <div>
          <Label className="text-xs uppercase tracking-widest text-neutral-500">Document type</Label>
          <div className="mt-2 grid grid-cols-2 gap-2 p-1 bg-neutral-100 rounded-full" data-testid="bill-doc-type-toggle">
            {[
              { key: "bill", label: "Bill", icon: Receipt, active: "bg-[#0F2F1E] text-white shadow" },
              { key: "challan", label: "Challan", icon: FileText, active: "bg-[#4238C1] text-white shadow" },
            ].map((opt) => {
              const Icon = opt.icon;
              const active = form.doc_type === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, doc_type: opt.key }))}
                  data-testid={`bill-doc-type-${opt.key}`}
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-semibold transition-all ${
                    active ? opt.active : "text-neutral-600 hover:text-neutral-900"
                  }`}
                >
                  <Icon size={14} strokeWidth={2} />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Employee *</Label>
            <Select value={form.employee_id} onValueChange={(v) => setForm((f) => ({ ...f, employee_id: v, buyer_id: "" }))}>
              <SelectTrigger className="hygenix-input" data-testid="bill-employee-select">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {employees.map((e) => (<SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Buyer *</Label>
            <Select value={form.buyer_id} onValueChange={(v) => setForm((f) => ({ ...f, buyer_id: v }))} disabled={!form.employee_id}>
              <SelectTrigger className="hygenix-input" data-testid="bill-buyer-select">
                <SelectValue placeholder={form.employee_id ? "Select" : "Pick employee first"} />
              </SelectTrigger>
              <SelectContent>
                {buyerOptions.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
              </SelectContent>
            </Select>
            {form.employee_id && buyers.length === 0 && (
              <p className="text-xs text-[#C4361B] mt-1">No buyers under {selectedEmployee?.name || "this employee"} yet.</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Bill / Challan #</Label>
            <Input value={form.bill_number || ""} onChange={(e) => setForm((f) => ({ ...f, bill_number: e.target.value }))} placeholder="auto if blank" className="hygenix-input" data-testid="bill-number-input" />
          </div>
          <div>
            <Label>Date *</Label>
            <Input type="date" value={form.bill_date} onChange={(e) => setForm((f) => ({ ...f, bill_date: e.target.value }))} className="hygenix-input" data-testid="bill-date-input" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Amount (₹) *</Label>
            <Input
              type="number"
              step="1"
              min="0"
              inputMode="numeric"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value.replace(/[^0-9]/g, "") }))}
              className="hygenix-input font-mono-num text-right text-lg"
              data-testid="bill-amount-input"
              placeholder="0"
            />
          </div>
          <div>
            <Label>{isEdit ? "Received so far (₹)" : "Received now (₹)"}</Label>
            <Input
              type="number"
              step="1"
              min="0"
              inputMode="numeric"
              value={form.paid_amount}
              disabled={isEdit}
              onChange={(e) => setForm((f) => ({ ...f, paid_amount: e.target.value.replace(/[^0-9]/g, "") }))}
              className="hygenix-input font-mono-num text-right text-lg"
              data-testid="bill-paid-amount"
              placeholder="0"
            />
            {isEdit && (
              <p className="text-xs text-neutral-500 mt-1" data-testid="bill-paid-amount-hint">
                Sum of the payments below. Record or remove a payment to change it.
              </p>
            )}
          </div>
        </div>

        <div className="rounded-2xl bg-[#FFF7ED] p-5 border border-[#F4C43B]/30">
          <div className="text-xs uppercase tracking-widest text-[#B7791F] font-bold mb-3">
            Assign collection follow-up
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Given to employee</Label>
              <Select
                value={form.follow_up_employee_id || ""}
                onValueChange={(v) => setForm((f) => ({ ...f, follow_up_employee_id: v }))}
              >
                <SelectTrigger className="hygenix-input" data-testid="bill-followup-employee">
                  <SelectValue placeholder="Same as employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (<SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Given at which date</Label>
              <Input
                type="date"
                value={form.follow_up_date || ""}
                onChange={(e) => setForm((f) => ({ ...f, follow_up_date: e.target.value }))}
                className="hygenix-input"
                data-testid="bill-followup-date"
              />
            </div>
          </div>
          <p className="text-xs text-neutral-600 mt-3">
            Which employee is responsible for collecting from this buyer, and the date it was handed to them.
          </p>
        </div>

        {isEdit && (
          <div>
            <Label>Order status</Label>
            <Select value={form.order_status || "pending"} onValueChange={(v) => setForm((f) => ({ ...f, order_status: v }))}>
              <SelectTrigger className="hygenix-input" data-testid="bill-order-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="dispatched">Dispatched</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="on_hold">On hold</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div>
          <Label>Notes</Label>
          <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} className="hygenix-input" data-testid="bill-notes" />
        </div>

        {/* Balance strip */}
        <div className="flex items-center justify-between p-5 rounded-2xl bg-gradient-to-br from-[#FFF7ED] to-[#FFE8D6]">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-600 font-bold">Balance</div>
            <div className={`font-mono-num text-3xl font-bold ${balance > 0 ? "text-[#C4361B]" : "text-[#175C3B]"}`}
                 data-testid="bill-balance-display">
              {inr(balance)}
            </div>
          </div>
          <div className="text-right text-xs text-neutral-700">
            Amount {inr(amount)}<br />Paid {inr(paid)}
          </div>
        </div>

        <Button type="submit" disabled={busy} className="w-full btn-primary py-6 text-base" data-testid="bill-submit-btn">
          {busy ? "Saving…" : isEdit ? "Save changes" : `Create ${form.doc_type === "challan" ? "challan" : "bill"}`}
        </Button>
      </form>

      {isEdit && (
        <div className="mt-6 card-surface p-6 space-y-3 animate-fade-up">
          <h3 className="font-display text-2xl font-semibold">Record part-payment</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <div>
              <Label className="text-xs">Amount received (₹)</Label>
              <Input
                type="number"
                step="1"
                min="0"
                inputMode="numeric"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="0"
                className="hygenix-input font-mono-num text-right"
                data-testid="bill-add-payment-amount"
              />
            </div>
            <div>
              <Label className="text-xs">Payment date</Label>
              <Input
                type="date"
                value={payDate}
                onChange={(e) => setPayDate(e.target.value)}
                className="hygenix-input"
                data-testid="bill-add-payment-date"
              />
            </div>
            <div>
              <Label className="text-xs">Mode</Label>
              <Select value={payMode} onValueChange={setPayMode}>
                <SelectTrigger className="hygenix-input" data-testid="bill-add-payment-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank">Bank</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Note</Label>
              <Input
                type="text"
                value={payNote}
                onChange={(e) => setPayNote(e.target.value)}
                placeholder="e.g. cheque #123"
                className="hygenix-input"
                data-testid="bill-add-payment-note"
              />
            </div>
            <div className="flex items-end">
              <Button type="button" onClick={addPayment} disabled={payBusy} className="btn-accent w-full h-[42px]" data-testid="bill-add-payment-btn">
                {payBusy ? "Saving…" : "Record"}
              </Button>
            </div>
          </div>
          <p className="text-xs text-neutral-500">
            Cannot be more than the pending balance. Payment mode + date are stored on the ledger.
          </p>

          {payments.length > 0 && (
            <div className="mt-5 border-t border-neutral-100 pt-4">
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold mb-2">
                Payment history
              </div>
              <table className="w-full text-sm" data-testid="payment-history-table">
                <thead>
                  <tr className="text-left text-neutral-500 text-[11px] uppercase tracking-wider">
                    <th className="py-2">Date</th>
                    <th className="py-2">Mode</th>
                    <th className="py-2 text-right">Amount</th>
                    <th className="py-2">Note</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {[...payments].reverse().map((p, i) => (
                    <tr key={p.id || `${p.date}-${p.amount}-${p.mode}-${i}`} className={`border-t border-neutral-100 ${p.deleted ? "opacity-40 line-through" : ""}`} data-testid={`payment-row-${i}`}>
                      <td className="py-2 font-mono-num">{(p.date || "").slice(0, 10)}</td>
                      <td className="py-2 capitalize">
                        <span className="chip bg-neutral-100 text-neutral-700">{p.mode || "cash"}</span>
                      </td>
                      <td className="py-2 text-right font-mono-num font-semibold">{inr(p.amount)}</td>
                      <td className="py-2 text-neutral-500">{p.note || "—"}{p.deleted ? " (removed)" : ""}</td>
                      <td className="py-2 text-right">
                        {isOwner && p.id && !p.deleted && (
                          <button type="button" onClick={() => removePayment(p)} className="text-neutral-400 hover:text-[#C4361B]" data-testid={`payment-remove-${p.id}`} aria-label="Remove payment">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
